import { normalizeValue } from '../../../features/sentiment';

export const getEffectiveMessageCount = (session) => {
  const rawAggregateCount = session?.['count(messages)'];
  const parsedAggregateCount = (() => {
    if (typeof rawAggregateCount === 'number') return rawAggregateCount;
    if (typeof rawAggregateCount === 'string') {
      const parsed = Number(rawAggregateCount);
      return Number.isFinite(parsed) ? parsed : null;
    }
    if (rawAggregateCount && typeof rawAggregateCount === 'object') {
      const candidates = [
        rawAggregateCount.count,
        rawAggregateCount.messages,
        ...Object.values(rawAggregateCount)
      ];
      for (const candidate of candidates) {
        const parsed = Number(candidate);
        if (Number.isFinite(parsed)) return parsed;
      }
    }
    return null;
  })();

  const fallbackStoredCount = Number(session.messages_count);
  return Number.isFinite(parsedAggregateCount)
    ? parsedAggregateCount
    : (Number.isFinite(fallbackStoredCount) ? fallbackStoredCount : 0);
};

export const applyInboxSessionFilters = (sessions, {
  topicsFilter = '',
  labelsFilter = '',
  selectedFilters,
  hideSingleMessage = false,
  includePriorityFilter = true
} = {}) => {
  let filteredSessions = [...sessions];

  if (topicsFilter) {
    filteredSessions = filteredSessions.filter((session) => {
      if (!session.rawSentimentTopics) return false;

      try {
        let topicsData = session.rawSentimentTopics;
        if (typeof topicsData === 'string') {
          topicsData = JSON.parse(topicsData);
        }

        if (topicsData.primary_category?.toLowerCase().includes(topicsFilter.toLowerCase())) {
          return true;
        }

        if (topicsData.user_intent?.toLowerCase().includes(topicsFilter.toLowerCase())) {
          return true;
        }

        if (topicsData.topics && Array.isArray(topicsData.topics)) {
          return topicsData.topics.some((topic) => topic.toLowerCase().includes(topicsFilter.toLowerCase()));
        }

        return false;
      } catch {
        return false;
      }
    });
  }

  if (labelsFilter) {
    filteredSessions = filteredSessions.filter((session) => {
      if (!session.rawSentimentLabels) return false;

      try {
        let labelsData = session.rawSentimentLabels;
        if (typeof labelsData === 'string') {
          labelsData = JSON.parse(labelsData);
        }

        if (Array.isArray(labelsData)) {
          return labelsData.some((label) =>
            typeof label === 'string' && label.toLowerCase().includes(labelsFilter.toLowerCase())
          );
        }

        return false;
      } catch {
        return false;
      }
    });
  }

  if (selectedFilters.primary_category.length > 0) {
    filteredSessions = filteredSessions.filter((session) => {
      if (session.primaryIntent) {
        return selectedFilters.primary_category.includes(session.primaryIntent);
      }
      if (!session.rawSentimentTopics) return false;
      try {
        let topicsData = session.rawSentimentTopics;
        if (typeof topicsData === 'string') {
          topicsData = JSON.parse(topicsData);
        }
        return selectedFilters.primary_category.includes(topicsData.primary_intent || topicsData.primary_category);
      } catch {
        return false;
      }
    });
  }

  if (selectedFilters.urgency_level.length > 0) {
    filteredSessions = filteredSessions.filter((session) => {
      if (!session.rawSentimentTopics) return false;
      try {
        let topicsData = session.rawSentimentTopics;
        if (typeof topicsData === 'string') {
          topicsData = JSON.parse(topicsData);
        }
        return selectedFilters.urgency_level.includes(normalizeValue(topicsData.urgency_level));
      } catch {
        return false;
      }
    });
  }

  if (selectedFilters.resolution_status.length > 0) {
    filteredSessions = filteredSessions.filter((session) => {
      if (session.intentFulfilled) {
        const mapped = session.intentFulfilled === 'true'
          ? 'resolved'
          : (session.intentFulfilled === 'partial' ? 'partially_resolved' : 'unresolved');
        return selectedFilters.resolution_status.includes(mapped);
      }
      return false;
    });
  }

  if (includePriorityFilter && selectedFilters.priority.length > 0) {
    filteredSessions = filteredSessions.filter((session) =>
      session.priority && selectedFilters.priority.includes(session.priority)
    );
  }

  if (selectedFilters.emotions.length > 0) {
    filteredSessions = filteredSessions.filter((session) => {
      if (!session.rawSentimentLabels) return false;
      try {
        let labelsData = session.rawSentimentLabels;
        if (typeof labelsData === 'string') {
          labelsData = JSON.parse(labelsData);
        }
        if (Array.isArray(labelsData)) {
          return selectedFilters.emotions.some((emotion) =>
            labelsData.some((label) => {
              if (typeof label === 'string') {
                return label.toLowerCase().includes(emotion.toLowerCase()) ||
                  label.toLowerCase() === emotion.toLowerCase();
              }
              return false;
            })
          );
        }
        return false;
      } catch {
        return false;
      }
    });
  }

  if (hideSingleMessage) {
    filteredSessions = filteredSessions.filter((session) => session.messageCount > 1);
  }

  return filteredSessions;
};
